import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { resizeImage } from "../../../services/screenshot.service"
import { useSlopedRoof } from "../../../store/sloped-roof/hooks"
import { useModal } from "../../../store/modal/hooks"
import { renderer } from "../../viewer/Renderer"
import { useApplication } from "../../../store/application/hooks"
import { isMobile } from "../../../services/helpers.service"
import { useAccountState } from "../../providers/AccountStateProvider"
import { useQueryParams } from "../../../store/query/sloped-roof.hooks"
import { SlopedRoofData } from "../../../store/sloped-roof/store"
import { Models } from "../../viewer/renderer.api"
import { ApplicationTypes } from "../../../store/application/store"

type VisibleState = {
  roofType: boolean
  roofInsulation: boolean
  roofInsulationFoil: boolean
  roofVaporBarrier: boolean
  roofConstruction: boolean
  details: boolean
  // SKYLIGHT
  skylight: boolean
  skylightGutter: boolean
  skylightAccessories: boolean
  // CHIMNEY
  chimneyTransitPan: boolean
  chimneyDrain: boolean
  // FOOTING BEAM
  footingBeamGutter: boolean
  // VALLEY GUTTER
  valleyGutter: boolean
  valleyGutterCarpentry: boolean
  valleyGutterGutter: boolean
  // RIDGE
  ridgeSlab: boolean
  ridgeBeam: boolean
}

export function useOverview() {
  const account = useAccountState()
  const { t, i18n } = useTranslation() as any
  const [linkCopied, setLinkCopied] = useState<boolean>(false)
  const {
    configurationState: { roofType, roofInsulation },
    thermalPerformance,
    setOverviewScreenshot,
    setHumanReadableName,
  } = useSlopedRoof()

  const roofTypeType = roofType.type!
  const roofInsulationType = roofInsulation.type!

  const {
    displaySlopedRoofDownloadModal,
    displaySlopedRoofThermalCalculationModal,
    displayRequestAdviceModal,
    displaySaveConfigurationModal,
    displaySaveConfigurationSuccessModal,
  } = useModal()
  const {
    currentModel,
    applicationType,
    directLink,
    setDirectLink,
  } = useApplication()
  const { setQueryParams } = useQueryParams()

  const initialVisibleState = useMemo(
    () => ({
      roofType: true,
      roofInsulation: true,
      roofInsulationFoil: true,
      roofVaporBarrier: true,
      roofConstruction: true,
      details: true,
      // SKYLIGHT
      skylight: true,
      skylightGutter: true,
      skylightAccessories: true,
      // CHIMNEY
      chimneyTransitPan: true,
      chimneyDrain: true,
      // FOOTING BEAM
      footingBeamGutter: true,
      // VALLEY GUTTER
      valleyGutter: true,
      valleyGutterCarpentry: true,
      valleyGutterGutter: true,
      // RIDGE
      ridgeSlab: true,
      ridgeBeam: true,
    }),
    []
  )
  const [visibleState, setVisibleState] = useState<VisibleState>(
    initialVisibleState
  )
  const humanReadableName = `${roofType.type} / ${roofInsulation.type}`

  useEffect(() => {
    setHumanReadableName(humanReadableName)
  }, [humanReadableName, setHumanReadableName])

  useEffect(() => {
    if (!directLink) {
      const data: SlopedRoofData = {
        roofType,
        roofInsulation,
        thermalPerformance,
      }
      setQueryParams(data, i18n.language)
    }

    // Handle direct link
    if (renderer && currentModel === Models.DOLLHOUSE && directLink) {
      setDirectLink(false)
      setTimeout(() => {
        let model = Models.SLOPED_ROOF
        renderer.displayModel(model).then(() => {
          renderer.displaySlopedRoofType(roofTypeType)
          renderer.toggleLayer("DAKBEDEKKING", true)
          renderer.displaySlopedRoofInsulation(roofInsulationType)
          renderer.toggleLayer("ISOLATIE", true)
        })
      }, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thermalPerformance, roofType, roofInsulation, setQueryParams, directLink])

  useEffect(
    () => {
      if (!isMobile()) renderer.displayMarkers()
      const dataURL = renderer.createScreenshot()
      resizeImage(dataURL, 520, 420).then((resizedDataURL) => {
        setOverviewScreenshot(resizedDataURL)
      })

      return () => {
        renderer.hideMarkers()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setOverviewScreenshot]
  )

  function toggleVisibleState(key: keyof VisibleState) {
    setVisibleState({ ...visibleState, [key]: !visibleState[key] })
  }

  useEffect(() => {
    setVisibleState(initialVisibleState)
    if (!isMobile()) renderer.displayMarkers()
    renderer.toggleAllLayers(applicationType, true)
  }, [currentModel, setVisibleState, initialVisibleState, applicationType])

  function handleToggleClick(toggle = "roofType", layer = "DAKBEDEKKING") {
    toggleVisibleState(toggle as keyof VisibleState)
    renderer.toggleLayers(layer)
  }

  function handleDetailsClick() {
    toggleVisibleState("details")
    renderer.toggleLayers("DAKDETAILS")
    renderer.toggleDetails()
  }

  function handleAdjustSpecificationsClick() {
    displaySlopedRoofThermalCalculationModal()
  }

  function handleDownloadFilesClick() {
    displaySlopedRoofDownloadModal()
  }

  function handleRequestAdviceClick() {
    displayRequestAdviceModal()
  }

  const copyShareLink = () => {
    const link = window.location.href
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(link).then(() => setLinkCopied(true))
    } else {
      window.prompt(t("flatRoof.overview.shareConfiguration"), link)
      setLinkCopied(true)
    }
  }

  function handleSaveConfigurationClick() {
    if (!account.isAuthenticated) return

    displaySaveConfigurationModal(async (name: string) => {
      const slopedRoofData: SlopedRoofData = {
        roofType: roofType,
        roofInsulation: roofInsulation,
        thermalPerformance: thermalPerformance,
      }
      const slopedRoofConfiguration = {
        application_type: ApplicationTypes.SLOPED_ROOF,
        application_data: slopedRoofData,
        display_name: name ?? `${t("home.slopedRoof")}`,
        language: i18n.language,
      }
      account
        .saveConfiguration(slopedRoofConfiguration)
        .then((response) => {
          displaySaveConfigurationSuccessModal(
            response?.saved_configuration?.record_id
          )
        })
        .catch(() => {
          console.error("Failed to save configuration")
        })
    })
  }

  return {
    t,
    currentModel,
    thermalPerformance,
    visibleState,
    linkCopied,

    roofTypeType,
    roofInsulationType,

    handleToggleClick,
    handleDetailsClick,

    handleAdjustSpecificationsClick,
    handleDownloadFilesClick,
    handleRequestAdviceClick,

    copyShareLink,
    handleSaveConfigurationClick,
  }
}
